import { Input } from "@/components/core/forms/input";
import { IconX } from "@tabler/icons-react";
import { useRef, useState } from "react";

interface DynamicTagsInputProps {
	value?: string[];
	onChange?: (tags: string[]) => void;
	placeholder?: string;
}

export const DynamicTagsInput: React.FC<DynamicTagsInputProps> = ({ value = [], onChange, placeholder = "Add a tag..." }) => {
	const [tags, setTags] = useState<string[]>(value);
	const [inputValue, setInputValue] = useState("");
	const inputRef = useRef<HTMLInputElement>(null);

	const addTag = (tag: string) => {
		const newTag = tag.trim();
		if (!newTag || tags.includes(newTag)) return;
		const updatedTags = [...tags, newTag];
		setTags(updatedTags);
		onChange?.(updatedTags);
		setInputValue("");
	};

	const removeTag = (index: number) => {
		const updatedTags = tags.filter((_, i) => i !== index);
		setTags(updatedTags);
		onChange?.(updatedTags);
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if ((e.key === "Enter" || e.key === ",") && inputValue) {
			e.preventDefault();
			addTag(inputValue);
		} else if (e.key === "Backspace" && !inputValue && tags.length > 0) {
			removeTag(tags.length - 1);
		}
	};

	return (
		<div className="flex flex-row items-center flex-wrap min-h-[38px] pb-[4px] px-[2px] text-base rounded-md border-border border focus:border-focus-border focus:ring-2 ring-offset-0 focus:ring-focus-ring/30 outline-0 outline-none focus-within:ring-2 focus-within:ring-focus-ring/30 focus-within:border-focus-border">
			{tags.map((tag, index) => (
				<div
					key={index}
					className="inline-flex items-center justify-center h-[24px] max-h-[24px] ml-[4px] mt-[4px] px-2 rounded bg-slate-100 text-xs font-semibold text-foreground-dark leading-tight"
				>
					{tag}
					<button type="button" onClick={() => removeTag(index)} className="ml-1 text-foreground/50 hover:text-red-600">
						<IconX size={12} stroke={2} />
					</button>
				</div>
			))}
			<Input
				ref={inputRef}
				value={inputValue}
				onChange={(e) => setInputValue(e.target.value)}
				onKeyDown={handleKeyDown}
				onBlur={() => {
					if (inputValue.trim()) {
						addTag(inputValue);
					}
				}}
				className="h-[24px] mt-[4px] px-3 first-line:border-none shadow-none outline-none focus-visible:ring-0 focus-visible:ring-offset-0 w-auto flex-1 min-w-[100px] text-base text-foreground"
				placeholder={placeholder}
			/>
		</div>
	);
};
